/**
 * LoanDrilldownLoanLockTabV2 — restyled Loan Lock tab.
 *
 * A from-scratch reskin of the original LoanDrilldownLoanLockTab, matching the
 * carded "soft canvas + indigo accent + tabular-mono hero" design language:
 *   • the lock treated as one object moving through time (a single timeline)
 *   • the rate / countdown rendered as the hero number (tabular-nums mono)
 *   • a single always-correct primary action
 *
 * The original tab file is kept intact as a fallback — this is the live version
 * wired into the `loan-lock` route. All real wiring (data normalization,
 * permissions, sidebar dispatches, sizer preview, checklist navigation) is
 * preserved from the original.
 */
import {
  ArrowOutwardOutlined,
  CheckRounded,
  DescriptionOutlined,
  EditOutlined,
  ErrorOutlineOutlined,
  FlagOutlined,
  GppMaybeOutlined,
  HistoryOutlined,
  LockOpenOutlined,
  LockOutlined,
  PhotoCameraOutlined,
  QueryBuilderOutlined,
  HourglassEmptyOutlined,
  ScheduleOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  daysUntilDateOnly,
  formatDate,
} from "../../../../_src_shared/utils/functions/formatDate";
import { lockChannelOf } from "../../../../api/losLocksApi";
import { isPostCloseLoan } from "../../../../api/postCloseLockApi";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { useIsAcquisitions } from "../../../../pipelines/PipelineContext";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetKeyValue,
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import PostCloseLockPanel from "./PostCloseLockPanel";
import {
  LockNotesBlock,
  PreviousLocksCard,
  extensionEvents,
} from "./lockNotesDisplay";
import {
  getLockPricingMode,
  getLockStage,
  hasDealLock,
  isLockCurrentlyExpired,
  normalizeLockToCamel,
} from "./loanLockUtils";

/* ── design tokens (soft canvas · indigo accent · semantic green/amber/rose) ── */
const T = {
  ink: "#0F172A",
  slate: "#475569",
  muted: "#94A3B8",
  faint: "#CBD5E1",
  line: "#E2E8F0",
  lineSoft: "#EEF2F6",
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  accent: "#4338CA",
  accentSoft: "#dee5f9",
  // accentSoft: "#EEF2FF",
  accentLine: "#C7D2FE",
  green: "#059669",
  greenSoft: "#d9f8e9",
  // greenSoft: "#ECFDF5",
  greenLine: "#A7F3D0",
  amber: "#B45309",
  amberSoft: "#FFFBEB",
  amberLine: "#FDE68A",
  rose: "#BE123C",
  roseSoft: "#FFF1F2",
  roseLine: "#FECDD3",
  red: "#DC2626",
  redSoft: "#FEF2F2",
  redLine: "#FECACA",
  redInk: "#7F1D1D",
  redInk2: "#9B2C2C",
  orange: "#EA580C",
  orangeSoft: "#FFF7ED",
  orangeLine: "#FED7AA",
  orangeInk: "#9A3412",
};

// Numeric values keep tabular (evenly-spaced) figures but inherit the app's
// default font family, so they read consistently with the surrounding labels and
// text instead of switching to a monospace face.
const NUM = { fontVariantNumeric: "tabular-nums" };

// RTL base-rate product labels (lock.rtl_product is a product key).
const RTL_PRODUCT_LABELS = {
  fix_and_flip: "Fix and Flip",
  fix_and_flip_heavy_rehab: "Fix and Flip — Heavy Rehab",
  ground_up_construction: "Ground Up Construction",
  bridge: "Bridge",
};

/* ── primitives ────────────────────────────────────────────────────────── */

const TONES = {
  amber: { bg: T.amberSoft, fg: T.amber, bd: T.amberLine },
  green: { bg: T.greenSoft, fg: T.green, bd: T.greenLine },
  accent: { bg: T.accentSoft, fg: T.accent, bd: T.accentLine },
  rose: { bg: T.roseSoft, fg: T.rose, bd: T.roseLine },
  red: { bg: T.redSoft, fg: T.red, bd: T.redLine },
  orange: { bg: T.orangeSoft, fg: T.orange, bd: T.orangeLine },
  slate: { bg: "#F1F5F9", fg: T.slate, bd: T.line },
};

const Tag = ({ tone = "slate", dot, icon, lock, children }) => {
  const c = TONES[tone] || TONES.slate;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        fontSize: 12,
        fontWeight: 600,
        color: c.fg,
        background: c.bg,
        border: `1px solid ${c.bd}`,
        px: 1.25,
        py: "3px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        "& svg": { fontSize: 14 },
      }}
    >
      {dot && (
        <Box
          component="span"
          sx={{ width: 6, height: 6, borderRadius: "999px", background: c.fg }}
        />
      )}
      {lock && (
        <LockOutlined
          sx={{ fontSize: 14, color: c.fg }}
          // component="span"
          // sx={{ width: 6, height: 6, borderRadius: "999px", background: c.fg }}
        />
      )}
      {icon}
      {children}
    </Box>
  );
};

const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: T.surface,
      border: `1px solid ${T.line}`,
      borderRadius: "16px",
      p: 3,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionLabel = ({ children, sx }) => (
  <Box
    sx={{
      fontSize: 11.5,
      fontWeight: 700,
      color: T.muted,
      textTransform: "uppercase",
      letterSpacing: 0.7,
      mb: 1.75,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Line = ({ label, value, num, last }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 1.5,
      py: 1.25,
      borderBottom: last ? "none" : `1px solid ${T.line}`,
    }}
  >
    <Box sx={{ fontSize: 13.5, color: T.slate }}>{label}</Box>
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: isNil(value) || value === "—" ? T.faint : T.ink,
        textAlign: "right",
        ...(num && !isNil(value) ? NUM : {}),
      }}
    >
      {isNil(value) || value === "" ? "—" : value}
    </Box>
  </Box>
);

/* timeline event with a connecting rail */
const Event = ({ icon, tone, title, meta, body, muted, last }) => {
  const c = TONES[tone] || TONES.slate;
  return (
    <Box sx={{ display: "flex", gap: 1.75, position: "relative" }}>
      {!last && (
        <Box
          sx={{
            position: "absolute",
            left: 16,
            top: 34,
            bottom: -6,
            width: 2,
            background: T.line,
          }}
        />
      )}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "999px",
          background: c.bg,
          color: c.fg,
          border: `1px solid ${tone === "slate" ? T.line : "transparent"}`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          zIndex: 1,
          "& svg": { fontSize: 17 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, pb: last ? 0 : 4.5, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              fontSize: 14.5,
              fontWeight: 600,
              color: muted ? T.muted : T.ink,
            }}
          >
            {title}
          </Box>
          {meta && <Box sx={{ fontSize: 12.5, color: T.muted }}>{meta}</Box>}
        </Box>
        {body && (
          <Box
            sx={{ fontSize: 13.5, color: T.slate, mt: 0.5, lineHeight: 1.5 }}
          >
            {body}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const LinkText = ({ children, onClick, withIcon }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      font: "inherit",
      fontSize: 13,
      fontWeight: 600,
      color: T.accent,
      background: "transparent",
      border: "none",
      p: 0,
      cursor: "pointer",
      whiteSpace: "nowrap",
      "&:hover": { textDecoration: "underline" },
      "& svg": { fontSize: 14 },
    }}
  >
    {children}
    {withIcon && <ArrowOutwardOutlined />}
  </Box>
);

const PrimaryBtn = ({ children, onClick, color = T.accent, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      background: color,
      color: "#fff",
      border: "none",
      px: 2.5,
      py: 1.4,
      borderRadius: "10px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      "& svg": { fontSize: 16 },
      "&:hover": { filter: "brightness(0.94)" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const GhostBtn = ({ children, onClick, sx }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.75,
      background: T.surface,
      color: T.slate,
      border: `1px solid ${T.line}`,
      // px: 2,
      // py: 1,
      px: 2.5,
      py: 1.4,
      borderRadius: "10px",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap",
      "& svg": { fontSize: 14 },
      "&:hover": { background: "#F8FAFC" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

/* ── helpers ───────────────────────────────────────────────────────────── */

// Big-number rate string ("6.500") without the trailing %.
const heroPct = (v) => {
  if (isNil(v)) return null;
  const s = formatPercent(v);
  return typeof s === "string" ? s.replace("%", "") : s;
};

const lockRateOf = (loanLock, isRtl) =>
  isRtl
    ? (loanLock?.coupon_rate ?? loanLock?.lock_buy_rate)
    : loanLock?.rate_at_lock;

// Whole calendar days from today (Eastern) to the expiration's calendar date.
// The expiration is date-only (stored as midnight UTC), so diffing raw
// instants and ceil-ing counted the partial day left today as a full one — a
// 30-day lock read "31 days" on the day it was confirmed.
const daysUntil = (date) => (isNil(date) ? null : daysUntilDateOnly(date));

// Exceptions still open/under review (not Approved/Rejected). Prefer the lock's
// snapshot (exceptions_at_lock) and fall back to the loan's current exceptions.
const OPEN_EXCEPTION_STATUSES = [
  "Open",
  "In Review",
  "In Review - Pending Credit Committee",
];
const getOpenExceptions = (loanLock, loan) => {
  const snap = loanLock?.exceptions_at_lock ?? loanLock?.exceptionsAtLock;
  const atLock = Array.isArray(snap) && snap.length > 0;
  const source = atLock
    ? snap
    : Array.isArray(loan?.exceptions)
      ? loan.exceptions
      : [];
  const items = source.filter((e) =>
    OPEN_EXCEPTION_STATUSES.includes(e?.exceptionStatus || "Open"),
  );
  return { items, atLock };
};

// exceptionCategory can be a string or a list — render a readable label.
const exceptionCategoryLabel = (cat) =>
  Array.isArray(cat) ? cat.filter(Boolean).join(", ") : cat || "Exception";

/* ═══════════════════════════════════════════
   HERO — rate / countdown as the signature number
   ═══════════════════════════════════════════ */
const Hero = ({ stage, loanLock, rateValue, rateIsFallback, actionNeeded }) => {
  const rate = heroPct(rateValue);
  const tier = loanLock?.margin_tier;

  if (stage === 0) {
    return (
      <Box>
        <SectionLabel sx={{ mb: 1.75 }}>Rate lock</SectionLabel>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          <Box
            sx={{
              ...NUM,
              fontSize: 52,
              fontWeight: 700,
              color: T.faint,
              lineHeight: 1,
            }}
          >
            —.———
          </Box>
          <Box sx={{ fontSize: 16, color: T.muted }}>%</Box>
        </Box>
        <Box
          sx={{
            fontSize: 14,
            color: T.slate,
            mt: 1.25,
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          No lock yet. Request one to capture pricing and snapshot the loan as
          it stands today.
        </Box>
      </Box>
    );
  }

  if (stage === 1) {
    const flagged = !!actionNeeded;
    return (
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            mb: 1.75,
            flexWrap: "wrap",
          }}
        >
          <SectionLabel sx={{ mb: 0 }}>Requested rate</SectionLabel>
          {flagged ? (
            <Tag tone="red" dot>
              Returned for action
            </Tag>
          ) : (
            <Tag tone="amber" dot>
              Awaiting confirmation
            </Tag>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box
            sx={{
              ...NUM,
              fontSize: 52,
              fontWeight: 700,
              color: T.ink,
              lineHeight: 1,
            }}
          >
            {rate || "—.———"}
          </Box>
          <Box sx={{ fontSize: 22, color: T.slate, fontWeight: 600 }}>%</Box>
        </Box>
        <Box sx={{ fontSize: 14, color: T.slate, mt: 1.25, lineHeight: 1.5 }}>
          {flagged
            ? "Sent back to the requester. Resolve the flag or file an exception before it can be confirmed."
            : `${
                loanLock?.lockRequestUser?.fullName
                  ? `Requested by ${loanLock.lockRequestUser.fullName}`
                  : "Requested"
              }${
                loanLock?.lockRequestedDate
                  ? ` on ${formatDate(loanLock.lockRequestedDate)}`
                  : ""
              }. The desk sets counterparty and final expiration on confirm.`}
        </Box>
      </Box>
    );
  }

  // stage 2 (active) / 3 (expired)
  const expired = stage === 3;
  const days = daysUntil(loanLock?.lockExpirationDate);
  return (
    <Box sx={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      <Box>
        <SectionLabel sx={{ mb: 1.75 }}>Locked rate</SectionLabel>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box
            sx={{
              ...NUM,
              fontSize: 52,
              fontWeight: 700,
              color: T.ink,
              lineHeight: 1,
            }}
          >
            {rate || "—.———"}
          </Box>
          <Box sx={{ fontSize: 22, color: T.slate, fontWeight: 600 }}>%</Box>
        </Box>
        <Box sx={{ mt: 1.5 }}>
          {expired ? (
            <Tag tone="rose" dot>
              Expired
            </Tag>
          ) : (
            <Tag tone="green" lock>
              {tier ? `Locked · ${tier} tier` : "Locked"}
            </Tag>
          )}
        </Box>
        {rate && rateIsFallback && (
          <Box sx={{ fontSize: 11.5, color: T.muted, mt: 1 }}>
            From current loan terms — no rate captured on the lock.
          </Box>
        )}
      </Box>
      <Box sx={{ borderLeft: `1px solid ${T.line}`, pl: 7 }}>
        <SectionLabel sx={{ mb: 1.75 }}>
          {expired ? "Expired" : "Expires in"}
        </SectionLabel>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Box
            sx={{
              ...NUM,
              fontSize: 52,
              fontWeight: 700,
              color: expired ? T.rose : T.ink,
              lineHeight: 1,
            }}
          >
            {days == null ? "—" : Math.abs(days)}
          </Box>
          <Box
            sx={{
              fontSize: 22,
              color: T.slate,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
            }}
          >
            {expired ? "days ago" : "days"}
            <QueryBuilderOutlined
              sx={{ fontSize: 20, color: T.slate, ml: 0.75, mb: -0.25 }}
            />
          </Box>

          {/* <Box sx={{ fontSize: 22, color: T.slate, fontWeight: 600 }}>%</Box> */}
        </Box>
        <Box sx={{ fontSize: 13.5, color: T.slate, mt: 1.5 }}>
          {loanLock?.lockExpirationDate
            ? formatDate(loanLock.lockExpirationDate)
            : "No expiration set"}
          {loanLock?.lock_period_days
            ? ` · ${loanLock.lock_period_days}-day lock`
            : ""}
        </Box>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════ */
const LoanDrilldownLoanLockTabV2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canViewLoanLock, isCapitalMarkets } = usePermissionsHook();
  const isAcquisitions = useIsAcquisitions();
  const loan = useSelector((state) => state.loanDrilldown);

  const rawLock = loan?.loanLock;
  const loanLock = useMemo(() => normalizeLockToCamel(rawLock), [rawLock]);
  const isExpired = useMemo(() => isLockCurrentlyExpired(loanLock), [loanLock]);
  const stage = useMemo(() => getLockStage(rawLock), [rawLock]);

  const product =
    loanLock?.product ||
    (getLockPricingMode(loan?.loanProductType) === "price" ? "dscr" : "rtl");
  const isRtl = product === "rtl";

  // Salesforce-synced locks (source: "salesforce_rate_lock") don't carry the
  // rate on the lock doc, so fall back to the loan's note rate — for a confirmed
  // lock that IS the locked rate. `rateIsFallback` marks the substitution.
  const lockOwnRate = lockRateOf(loanLock, isRtl);
  const rateValue = isNil(lockOwnRate) ? loan?.interestRate : lockOwnRate;
  const rateIsFallback = isNil(lockOwnRate) && !isNil(loan?.interestRate);

  const actionNeeded = loanLock?.actionNeeded?.reason
    ? loanLock.actionNeeded
    : null;
  const { items: openExceptions, atLock: exceptionsAtLock } = useMemo(
    () => getOpenExceptions(loanLock, loan),
    [loanLock, loan],
  );

  // UW, admin, capital markets, sales / loan officers, processing, closers.
  const canViewLock = canViewLoanLock;
  const canManage = isCapitalMarkets;

  // EV Post-Close loans don't lock through the LOS `/pricing` flow — locking is
  // Salesforce-direct over the EV_Post_Close population via the standalone
  // post-close tool. Hand the whole tab off to the dedicated panel (it runs its
  // own permission gate so capital markets can view too). Placed after all hooks
  // above so the hook order stays stable.
  //
  // The gate is the ACQUISITIONS pipeline + `isPostCloseLoan`, never a "Closed"
  // status: EV_Post_Close opps live in loan_acquisitions, so a preclose deal is
  // never post-close however far it has funded. Gating on status sent every
  // funded loan to the Salesforce lookup and 404'd it.
  // …unless the deal already carries a lock from the deal-keyed store, live or
  // expired (an acquisition's Salesforce Rate_Lock__c is synced there): the
  // post-close panel can't see that lock, so the standard view shows and
  // manages it — including Extend on an expired one.
  const isPostClose =
    isAcquisitions && isPostCloseLoan(loan) && !hasDealLock(loan);
  if (isPostClose) {
    return (
      <LoanDrilldownTabPanel>
        {/* Request / confirm / cancel open in the Loan Lock sidebar — the same
            container the pre-close lock form uses. */}
        <PostCloseLockPanel
          loan={loan}
          onOpenForm={(postCloseMode) => {
            dispatch(sidebarSetKeyValue("params", { postCloseMode }));
            dispatch(
              sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK),
            );
          }}
        />
      </LoanDrilldownTabPanel>
    );
  }

  const goToExceptions = () => navigate("../exceptions");

  // The desk returned this lock asking for a pricing exception — open the
  // "Add Loan Exception" sidebar (fresh) so they can file it right here, rather
  // than bouncing them to the Exceptions tab. Null sidebar state = new exception.
  const openAddException = () => {
    dispatch(sidebarSetStateValue(null));
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EXCEPTION));
  };

  const openLockSidebar = () => {
    dispatch(sidebarSetKeyValue("params", null));
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK));
  };
  const openEditLock = () => {
    dispatch(sidebarSetKeyValue("params", { mode: "edit" }));
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK));
  };

  if (!canViewLock) {
    return (
      <LoanDrilldownTabPanel>
        <Box sx={{ background: T.bg, minHeight: "100%", p: 3.5 }}>
          <Card sx={{ maxWidth: 1180, mx: "auto" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                py: 6,
              }}
            >
              <LockOutlined sx={{ fontSize: 36, color: T.faint }} />
              <Box sx={{ color: T.slate, fontSize: 13.5 }}>
                Visible to underwriting and admin roles only.
              </Box>
            </Box>
          </Card>
        </Box>
      </LoanDrilldownTabPanel>
    );
  }

  return (
    <LoanDrilldownTabPanel>
      <Box
        sx={{
          background: T.bg,
          minHeight: "100%",
          color: T.ink,
          p: { xs: 2, md: 3.5 },
        }}
      >
        <Box sx={{ maxWidth: 1180, mx: "auto" }}>
          {/* hero / header card */}
          <Card sx={{ mb: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 3,
                flexWrap: "wrap",
              }}
            >
              <Hero
                stage={stage}
                loanLock={loanLock}
                rateValue={rateValue}
                rateIsFallback={rateIsFallback}
                actionNeeded={actionNeeded}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  alignItems: "flex-end",
                }}
              >
                {stage === 0 && (
                  <PrimaryBtn onClick={openLockSidebar}>
                    <LockOutlined /> Request a lock
                  </PrimaryBtn>
                )}
                {stage === 1 &&
                  (canManage ? (
                    <>
                      <PrimaryBtn onClick={openLockSidebar} color={T.green}>
                        Confirm lock
                      </PrimaryBtn>
                      <Box
                        sx={{
                          fontSize: 12,
                          fontWeight: actionNeeded ? 600 : 400,
                          color: actionNeeded ? T.red : T.muted,
                        }}
                      >
                        {actionNeeded
                          ? "Has open flag"
                          : "Capital markets only"}
                      </Box>
                    </>
                  ) : (
                    <>
                      <PrimaryBtn onClick={openLockSidebar}>
                        Update request
                      </PrimaryBtn>
                      <Box
                        sx={{
                          fontSize: 12,
                          fontWeight: actionNeeded ? 600 : 400,
                          color: actionNeeded ? T.red : T.muted,
                        }}
                      >
                        {actionNeeded
                          ? "Returned for action"
                          : "Awaiting capital markets"}
                      </Box>
                    </>
                  ))}
                {/* Manage (extend / cancel) is open to everyone; Edit details
                    stays capital-markets only. */}
                {(stage === 2 || stage === 3) && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {canManage && (
                      <GhostBtn onClick={openEditLock}>
                        <EditOutlined /> Edit details
                      </GhostBtn>
                    )}
                    <PrimaryBtn
                      onClick={openLockSidebar}
                      color={stage === 3 ? T.rose : T.accent}
                    >
                      {stage === 3 ? "Re-lock" : "Manage lock"}
                    </PrimaryBtn>
                  </Box>
                )}
              </Box>
            </Box>

            {/* mini key/value strip */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 6, md: 7.5 },
                // gap: { xs: 3, md: 4.5 },
                pt: 2.75,
                mt: 2.75,
                borderTop: `1px solid ${T.line}`,
                flexWrap: "wrap",
              }}
            >
              {[
                ["Product", loan?.loanProductType, false],
                ["Purpose", loan?.loanPurpose, false],
                [
                  "Loan amount",
                  isNil(loan?.totalLoanAmount)
                    ? null
                    : formatDollar(loan.totalLoanAmount),
                  true,
                ],
                [
                  "Interest rate",
                  isNil(loan?.interestRate)
                    ? null
                    : formatPercent(loan.interestRate),
                  true,
                ],
                ["Status", loan?.loanStatus, false],
              ].map(([k, v, num]) => (
                <Box key={k}>
                  <Box
                    sx={{
                      fontSize: 11,
                      color: T.muted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {k}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      mt: 0.5,
                      color: isNil(v) ? T.faint : T.ink,
                      ...(num && !isNil(v) ? NUM : {}),
                    }}
                  >
                    {isNil(v) || v === "" ? "—" : v}
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>

          {/* returned-to-requester banner */}
          {actionNeeded && (
            <Box sx={{ mb: 2.5 }}>
              <ActionNeededBanner
                actionNeeded={actionNeeded}
                canManage={canManage}
                onPrimary={openLockSidebar}
                onFileException={openAddException}
              />
            </Box>
          )}

          {/* body grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: stage === 0 ? "1fr" : "1fr 320px",
              },
              gap: 2.5,
              alignItems: "start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {stage === 0 ? (
                <BeforeYouLockCard loan={loan} navigate={navigate} />
              ) : (
                <LockTimelineCard
                  loanLock={loanLock}
                  stage={stage}
                  isExpired={isExpired}
                  actionNeeded={actionNeeded}
                />
              )}
              {openExceptions.length > 0 && (
                <ExceptionsRail
                  exceptions={openExceptions}
                  atLock={exceptionsAtLock}
                  onOpen={goToExceptions}
                />
              )}
              <PreviousLocksCard
                dealId={loan?._id?.$oid || loan?._id}
                currentLockId={
                  stage !== 0 ? rawLock?._id?.$oid || rawLock?._id || null : null
                }
              />
            </Box>

            {stage !== 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <LockTermsRail
                  loanLock={loanLock}
                  stage={stage}
                  rateValue={rateValue}
                  rateIsFallback={rateIsFallback}
                />
                <SnapshotRail loanLock={loanLock} navigate={navigate} />
                {loanLock?.loanSizer?.file_display_name && (
                  <SizerRail
                    sizer={loanLock.loanSizer}
                    onOpen={() =>
                      dispatch(filePreviewSetData(loanLock.loanSizer))
                    }
                  />
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              mt: 2,
              background: T.amberSoft,
              border: `1px solid ${T.amberLine}`,
              borderRadius: "12px",
              p: "12px 14px",
            }}
          >
            <ErrorOutlineOutlined
              sx={{
                fontSize: 16,
                color: T.amber,
                flexShrink: 0,
                mt: "1px",
              }}
            />
            <Box sx={{ fontSize: 12.5, color: T.amber, lineHeight: 1.45 }}>
              Counterparty is visible to capital markets only and never appears
              here.
            </Box>
          </Box>

          {/* Escape hatch to the original (pre-redesign) Loan Lock tab. */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${T.line}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <GhostBtn onClick={() => navigate("../loan-lock-legacy")}>
              <HistoryOutlined /> View legacy
            </GhostBtn>
          </Box>
        </Box>
      </Box>
    </LoanDrilldownTabPanel>
  );
};

/* ── returned-to-requester banner ── */
const ActionNeededBanner = ({
  actionNeeded,
  canManage,
  onPrimary,
  onFileException,
}) => {
  const flaggedMeta = [
    actionNeeded.flaggedBy ? `Flagged by ${actionNeeded.flaggedBy}` : null,
    actionNeeded.flaggedDateTime
      ? formatDate(actionNeeded.flaggedDateTime)
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        flexWrap: "wrap",
        background: T.redSoft,
        border: `1px solid ${T.redLine}`,
        borderRadius: "14px",
        p: "16px 18px",
      }}
    >
      <Box
        sx={{
          // width: 34,
          // height: 34,
          borderRadius: "9px",
          // background: "#fff",
          // border: `1px solid ${T.redLine}`,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          color: T.red,
          "& svg": { fontSize: 24 },
        }}
      >
        <FlagOutlined />
      </Box>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontSize: 14.5, fontWeight: 700, color: T.red }}>
            Returned for action
          </Box>
          {actionNeeded.requiresException && (
            <Box
              component="span"
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: T.red,
                background: "#fff",
                border: `1px solid ${T.redLine}`,
                px: 1,
                py: "2px",
                borderRadius: "999px",
              }}
            >
              Exception required
            </Box>
          )}
        </Box>
        <Box sx={{ fontSize: 13.5, color: T.redInk, mt: 0.5, lineHeight: 1.5 }}>
          {actionNeeded.reason}
        </Box>
        {flaggedMeta && (
          <Box sx={{ fontSize: 12, color: T.redInk2, mt: 0.75 }}>
            {flaggedMeta}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        {actionNeeded.requiresException && (
          <Box
            component="button"
            type="button"
            onClick={onFileException}
            sx={{
              background: "#fff",
              color: T.red,
              border: `1px solid ${T.redLine}`,
              borderRadius: "9px",
              px: 1.75,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              "&:hover": { background: T.redSoft },
            }}
          >
            File exception
          </Box>
        )}
        <Box
          component="button"
          type="button"
          onClick={onPrimary}
          sx={{
            background: T.red,
            color: "#fff",
            border: "none",
            borderRadius: "9px",
            px: 1.75,
            py: 1,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": { filter: "brightness(0.94)" },
          }}
        >
          {canManage ? "Review lock" : "Update & resubmit"}
        </Box>
      </Box>
    </Box>
  );
};

/* ── right rail: open exceptions ── */
const ExceptionsRail = ({ exceptions, atLock, onOpen }) => (
  <Card sx={{ p: 0, overflow: "hidden", borderColor: T.orangeLine }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        background: T.orangeSoft,
        borderBottom: `1px solid ${T.orangeLine}`,
        px: 2.25,
        py: 1.5,
        "& svg": { fontSize: 16 },
      }}
    >
      <GppMaybeOutlined sx={{ color: T.orange }} />
      <Box sx={{ fontSize: 13, fontWeight: 700, color: T.orange }}>
        {exceptions.length} open exception{exceptions.length > 1 ? "s" : ""}
        {atLock ? " at lock" : ""}
      </Box>
    </Box>
    <Box sx={{ p: 2.25 }}>
      {exceptions.map((e, i) => (
        <Box
          key={e?._id || i}
          sx={{
            pt: i ? 1.75 : 0,
            mt: i ? 1.75 : 0,
            borderTop: i ? `1px solid ${T.line}` : "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ fontSize: 13.5, fontWeight: 700 }}>
              {exceptionCategoryLabel(e?.exceptionCategory)}
            </Box>
            {e?.exceptionStatus && <Tag tone="slate">{e.exceptionStatus}</Tag>}
          </Box>
          {e?.exceptionDescription && (
            <Box sx={{ fontSize: 12.5, color: T.slate, lineHeight: 1.5 }}>
              {e.exceptionDescription}
            </Box>
          )}
          {(e?.riskBucket || e?.assignedTo) && (
            <Box sx={{ fontSize: 11.5, color: T.muted, mt: 0.75 }}>
              {[
                e?.riskBucket ? `Risk: ${e.riskBucket}` : null,
                e?.assignedTo ? `Assigned ${e.assignedTo}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Box>
          )}
          {e?.compensatingFactors && !isEmpty(e.compensatingFactors) && (
            <Box sx={{ fontSize: 11.5, color: T.muted, mt: 0.25 }}>
              Compensating:{" "}
              {Array.isArray(e.compensatingFactors)
                ? e.compensatingFactors.join(", ")
                : e.compensatingFactors}
            </Box>
          )}
        </Box>
      ))}
      <Box sx={{ mt: 1.75 }}>
        <LinkText onClick={onOpen}>Open Exceptions tab →</LinkText>
      </Box>
    </Box>
  </Card>
);

/* ── lock timeline ── */
const LockTimelineCard = ({ loanLock, stage, actionNeeded }) => {
  const requester = loanLock?.lockRequestUser;
  const confirmer = loanLock?.lockConfirmUser;
  // A status line while awaiting confirmation, not a note — real notes get
  // their own block below the timeline.
  const requestedBody = stage === 1 ? "Pending pricing confirmation." : null;
  const extRows = extensionEvents(loanLock);

  return (
    <Card>
      <Box sx={{ fontSize: 16, fontWeight: 700, mb: 2.75 }}>Lock timeline</Box>

      <Event
        tone="green"
        icon={<CheckRounded />}
        title="Requested"
        meta={[
          requester?.fullName || "Not recorded",
          loanLock?.lockRequestedDate
            ? formatDate(loanLock.lockRequestedDate)
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
        body={requestedBody}
      />

      {stage === 1 && actionNeeded && (
        <>
          <Event
            tone="red"
            icon={<FlagOutlined />}
            title="Returned for action"
            meta={[
              actionNeeded.flaggedBy || null,
              actionNeeded.flaggedDateTime
                ? formatDate(actionNeeded.flaggedDateTime)
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            body={`${actionNeeded.reason}${
              actionNeeded.requiresException
                ? " Exception required before confirming."
                : ""
            }`}
          />
          <Event
            last
            muted
            tone="slate"
            icon={<LockOutlined />}
            title="Confirm"
            meta="blocked until resolved"
          />
        </>
      )}

      {stage === 1 && !actionNeeded && (
        <>
          <Event
            tone="amber"
            icon={<ScheduleOutlined />}
            title="Awaiting confirmation"
            meta="with capital-markets desk"
            body="The desk reviews price terms, enters the counterparty, and sets the final expiration."
          />
          <Event
            last
            muted
            tone="slate"
            icon={<LockOutlined />}
            title="Active"
            meta="not yet"
          />
        </>
      )}

      {(stage === 2 || stage === 3) && (
        <>
          <Event
            tone="green"
            icon={<CheckRounded />}
            title="Confirmed"
            meta={[
              confirmer?.fullName || "Not recorded",
              loanLock?.lockConfirmationDate
                ? formatDate(loanLock.lockConfirmationDate)
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            body={
              loanLock?.lockExpirationDate
                ? `Counterparty set, expiration finalized to ${formatDate(
                    loanLock.lockExpirationDate,
                  )}. Requester notified by email.`
                : "Counterparty set and expiration finalized. Requester notified by email."
            }
          />
          {extRows.map((r) => (
            <Event
              key={r.key}
              tone={r.tone}
              icon={<HistoryOutlined />}
              title={r.title}
              meta={r.meta}
              body={<Box sx={{ whiteSpace: "pre-wrap" }}>{r.body}</Box>}
            />
          ))}
          {stage === 2 ? (
            <Event
              last
              tone="accent"
              icon={<LockOutlined />}
              title="Active"
              meta={
                loanLock?.lockConfirmationDate
                  ? `since ${formatDate(loanLock.lockConfirmationDate)}`
                  : undefined
              }
              body={(() => {
                const d = daysUntil(loanLock?.lockExpirationDate);
                return d != null && d >= 0
                  ? `Loan is locked. ${d} days remain before expiration.`
                  : "Loan is locked.";
              })()}
            />
          ) : (
            <Event
              last
              tone="rose"
              icon={<WarningAmberOutlined />}
              title="Expired"
              meta={
                loanLock?.lockExpirationDate
                  ? formatDate(loanLock.lockExpirationDate)
                  : undefined
              }
              body="The lock has expired. Re-lock or request an extension to proceed to closing."
            />
          )}
        </>
      )}
      {loanLock?.lockNotes && (
        <LockNotesBlock notes={loanLock.lockNotes} />
      )}
    </Card>
  );
};

/* ── right rail: lock terms ── */
const LockTermsRail = ({ loanLock, stage, rateValue, rateIsFallback }) => {
  const rate = isNil(rateValue) ? null : formatPercent(rateValue);
  return (
    <Card sx={{ p: 2.5 }}>
      <SectionLabel>Lock terms</SectionLabel>
      <Line label="Rate" value={rate} num />
      {loanLock?.margin_tier && (
        <Line label="Margin tier" value={loanLock.margin_tier} />
      )}
      {loanLock?.margin_points_at_lock != null && (
        <Line
          label="Margin (pts)"
          value={`${+Number(loanLock.margin_points_at_lock).toFixed(3)} pts`}
          num
        />
      )}
      {loanLock?.rtl_product && (
        <Line
          label="RTL product"
          value={RTL_PRODUCT_LABELS[loanLock.rtl_product] || loanLock.rtl_product}
        />
      )}
      <Line label="Lock type" value={lockChannelOf(loanLock)} />
      <Line
        label="Period"
        value={
          loanLock?.lock_period_days
            ? `${loanLock.lock_period_days} days`
            : null
        }
      />
      <Line
        label="Requested"
        value={
          loanLock?.lockRequestedDate
            ? formatDate(loanLock.lockRequestedDate)
            : null
        }
      />
      <Line
        label="Confirmed"
        value={
          loanLock?.lockConfirmationDate
            ? formatDate(loanLock.lockConfirmationDate)
            : stage === 1
              ? "Pending"
              : null
        }
      />
      <Line
        label="Expires"
        value={
          loanLock?.lockExpirationDate
            ? formatDate(loanLock.lockExpirationDate)
            : stage === 1
              ? "Set on confirm"
              : null
        }
        num={!!loanLock?.lockExpirationDate}
        last={!loanLock?.pendingExtension}
      />
      {loanLock?.pendingExtension && (
        <Line
          label="Extension"
          value={
            loanLock.pendingExtension.extensionDays != null
              ? `Pending · ${loanLock.pendingExtension.extensionDays} days`
              : "Pending confirmation"
          }
          last
        />
      )}
      {rate && rateIsFallback && (
        <Box sx={{ fontSize: 11, color: T.muted, mt: 1.25, lineHeight: 1.4 }}>
          Rate reflects current loan terms — this lock didn’t capture one.
        </Box>
      )}
    </Card>
  );
};

/* ── right rail: snapshot at lock ── */
const SnapshotRail = ({ loanLock, navigate }) => {
  const snap = loanLock?.loanTermsAtLock;
  const has = !isNil(snap) && !isEmpty(snap);
  return (
    <Card sx={{ p: 2.5 }}>
      <SectionLabel>Snapshot at lock</SectionLabel>
      {!has ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            py: 2.5,
            px: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "11px",
              background: "#F1F5F9",
              color: T.faint,
              display: "grid",
              placeItems: "center",
              mb: 1.25,
              "& svg": { fontSize: 20 },
            }}
          >
            <PhotoCameraOutlined />
          </Box>
          <Box sx={{ fontSize: 13.5, fontWeight: 600, color: T.slate }}>
            No snapshot captured
          </Box>
          <Box
            sx={{
              fontSize: 12,
              color: T.muted,
              mt: 0.5,
              lineHeight: 1.5,
              maxWidth: 240,
            }}
          >
            This lock was synced without a terms snapshot. Compare against the
            loan’s current terms instead.
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <LinkText onClick={() => navigate("../terms")}>
              View current terms →
            </LinkText>
          </Box>
        </Box>
      ) : (
        <>
          <Line
            label="Total loan"
            value={
              isNil(snap.totalLoanAmount)
                ? null
                : formatDollar(snap.totalLoanAmount)
            }
            num
          />
          <Line
            label="Base amount"
            value={
              isNil(snap.baseLoanAmount)
                ? null
                : formatDollar(snap.baseLoanAmount)
            }
            num
          />
          <Line
            label="Interest rate"
            value={
              isNil(snap.interestRate) ? null : formatPercent(snap.interestRate)
            }
            num
          />
          <Line
            label="Term"
            value={
              isNil(snap.termMonths)
                ? null
                : `${formatNumber(snap.termMonths)} mo`
            }
            num
          />
          <Line
            label="Status"
            value={loanLock?.loanStatusAtLock || snap.loanStatus}
            last
          />
          <Box sx={{ mt: 1.75 }}>
            <LinkText onClick={() => navigate("../terms")}>
              View full terms →
            </LinkText>
          </Box>
        </>
      )}
    </Card>
  );
};

/* ── right rail: sizer ── */
const SizerRail = ({ sizer, onOpen }) => (
  <Card sx={{ p: 2.5 }}>
    <SectionLabel>Loan sizer</SectionLabel>
    <Box
      role="button"
      onClick={onOpen}
      title={sizer.file_display_name}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        color: T.accent,
        minWidth: 0,
        "&:hover .ll-sizer": { textDecoration: "underline" },
      }}
    >
      <DescriptionOutlined sx={{ fontSize: 18, flexShrink: 0 }} />
      <Box
        className="ll-sizer"
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {sizer.file_display_name}
      </Box>
    </Box>
  </Card>
);

/* ═══════════════════════════════════════════
   EMPTY STATE — "Before you lock"
   ═══════════════════════════════════════════ */
const BeforeYouLockCard = ({ loan, navigate }) => {
  const checklist = useMemo(() => {
    const sizer = loan?.loanSizer;
    const sizerAttached = !isEmpty(sizer);
    const rate = loan?.interestRate;
    const amount = loan?.totalLoanAmount;
    const termsConfirmed = !isNil(rate) && !isNil(amount);
    const status = loan?.loanStatus;
    const uwApproved = /clear to close|approved/i.test(String(status || ""));
    return [
      {
        t: "Attach a loan sizer",
        d: sizerAttached
          ? sizer?.file_display_name || "On file"
          : "Helps the desk price the lock accurately.",
        done: sizerAttached,
        link: sizerAttached ? null : { label: "Documents", to: "../documents" },
      },
      {
        t: "Confirm loan terms",
        d:
          [
            !isNil(rate) ? formatPercent(rate) : null,
            !isNil(amount) ? formatDollar(amount) : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Set the rate, term, and amount.",
        done: termsConfirmed,
        link: termsConfirmed ? null : { label: "Loan Terms", to: "../terms" },
      },
      {
        t: "Clear underwriting",
        d: status ? `Currently ${status}.` : "Underwriting status pending.",
        done: uwApproved,
        link: uwApproved ? null : { label: "Checklist", to: "../checklist" },
      },
    ];
  }, [loan]);

  return (
    <>
      <Card sx={{ textAlign: "center", p: "44px 24px" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "16px",
            background: T.accentSoft,
            color: T.accent,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
            "& svg": { fontSize: 26 },
          }}
        >
          <LockOpenOutlined />
        </Box>
        <Box sx={{ fontSize: 18, fontWeight: 700, mb: 0.75 }}>No lock set</Box>
        <Box
          sx={{
            mx: "auto",
            mb: 3,
            maxWidth: 400,
            fontSize: 13.5,
            color: T.slate,
            lineHeight: 1.55,
          }}
        >
          This loan hasn’t been locked yet. Request a lock to record pricing
          terms and capture a snapshot of the loan at lock time.
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <GhostBtn onClick={() => navigate("../sizer")}>Open sizer</GhostBtn>
        </Box>
      </Card>

      <Card>
        <Box sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>
          Before you lock
        </Box>
        <Box sx={{ fontSize: 13.5, color: T.muted, mb: 2.25 }}>
          Recommended, not required. You can request a lock at any time.
        </Box>
        {checklist.map((c, i) => (
          <Box
            key={c.t}
            sx={{
              display: "flex",
              gap: 1.75,
              alignItems: "flex-start",
              py: 1.6,
              borderTop: i ? `1px solid ${T.line}` : "none",
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "999px",
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                mt: "1px",
                background: c.done ? T.green : "#fff",
                border: `2px solid ${c.done ? T.green : T.faint}`,
                color: "#fff",
                "& svg": { fontSize: 14 },
              }}
            >
              {c.done && <CheckRounded />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 14, fontWeight: 600 }}>{c.t}</Box>
              <Box sx={{ fontSize: 13, color: T.muted, mt: "2px" }}>{c.d}</Box>
            </Box>
            {c.link && (
              <LinkText withIcon onClick={() => navigate(c.link.to)}>
                {c.link.label}
              </LinkText>
            )}
          </Box>
        ))}
      </Card>
    </>
  );
};

export default LoanDrilldownLoanLockTabV2;
